import { uiSurface } from "shared/ui/uiSurface"
import {
  Repeat,
} from "@mui/icons-material"
import {
  Box,
  Chip,
  Paper,
  Snackbar,
  Alert,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material"
import { DataGrid, GridColDef, GridPaginationModel, GridSortModel } from "@mui/x-data-grid"
import { FC, useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import { useGetRecurringOrdersQuery, RecurringOrderSummary } from "../../apis/appApi"
import { RecurringOrderDetailDrawer } from "./RecurringOrderDetailDrawer"

/** Helper: is a recurring order considered "stopped"? */
const isStopped = (endDate: string | null): boolean => {
  if (!endDate) return false
  const today = new Date().toISOString().split("T")[0]
  return endDate.split("T")[0] < today
}

/** Format a recurrence type for display using i18n key */
const formatFrequency = (
  recurrenceType: string,
  dailyRecurringCriteria: number | null,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string => {
  const key = `recurringOrders.frequency.${recurrenceType}`
  if (recurrenceType === "XDAYS" && dailyRecurringCriteria) {
    return t(key, { interval: dailyRecurringCriteria })
  }
  return t(key)
}

/** Format date string for display */
const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return "-"
  try {
    return new Date(dateStr).toLocaleDateString()
  } catch {
    return dateStr
  }
}

export const RecurringOrdersPage: FC = () => {
  const { t } = useTranslation()

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  })
  const [sortField, setSortField] = useState("dateCreated")
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc")
  const [statusFilter, setStatusFilter] = useState<string>("all")
  const [selectedId, setSelectedId] = useState<number | null>(null)

  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: "success" | "error"
  }>({ open: false, message: "", severity: "success" })

  const showSnackbar = useCallback(
    (message: string, severity: "success" | "error" = "success") => {
      setSnackbar({ open: true, message, severity })
    },
    [],
  )

  const { data: response, isLoading } = useGetRecurringOrdersQuery({
    page: paginationModel.page + 1,
    pageSize: paginationModel.pageSize,
    sortField,
    sortOrder,
    status: statusFilter,
  })

  const sortModel: GridSortModel = sortField
    ? [{ field: sortField, sort: sortOrder }]
    : []

  const handleSortModelChange = useCallback((model: GridSortModel) => {
    if (model.length > 0) {
      setSortField(model[0].field)
      setSortOrder(model[0].sort ?? "asc")
    } else {
      setSortField("dateCreated")
      setSortOrder("desc")
    }
    setPaginationModel((m) => ({ ...m, page: 0 }))
  }, [])

  const rows = response?.data ?? []
  const rowCount = response?.total ?? 0

  const columns: GridColDef<RecurringOrderSummary>[] = [
    {
      field: "client",
      headerName: t("recurringOrders.client"),
      flex: 1.5,
      minWidth: 160,
      sortable: false,
      valueGetter: (_value, row) => {
        const c = row.client
        return c ? `${c.firstName} ${c.lastName}` : "-"
      },
    },
    {
      field: "address",
      headerName: t("recurringOrders.address"),
      flex: 2,
      minWidth: 200,
      sortable: false,
      valueGetter: (_value, row) => {
        const a = row.address
        return a ? `${a.streetNumber} ${a.streetName}, ${a.city}` : "-"
      },
    },
    {
      field: "recurrenceType",
      headerName: t("recurringOrders.frequency"),
      flex: 1,
      minWidth: 140,
      valueGetter: (_value, row) =>
        formatFrequency(row.recurrenceType, row.dailyRecurringCriteria, t),
    },
    {
      field: "status",
      headerName: t("recurringOrders.status"),
      width: 110,
      sortable: false,
      renderCell: (params) => {
        const stopped = isStopped(params.row.endDate)
        return (
          <Chip
            size="small"
            label={stopped ? t("recurringOrders.stopped") : t("recurringOrders.active")}
            color={stopped ? "error" : "success"}
            variant="outlined"
          />
        )
      },
    },
    {
      field: "startDate",
      headerName: t("recurringOrders.startDate"),
      width: 120,
      valueGetter: (_value, row) => formatDate(row.startDate),
    },
    {
      field: "endDate",
      headerName: t("recurringOrders.endDate"),
      width: 120,
      valueGetter: (_value, row) => formatDate(row.endDate),
    },
    {
      field: "dateCreated",
      headerName: t("recurringOrders.created"),
      width: 120,
      valueGetter: (_value, row) => formatDate(row.dateCreated),
    },
  ]

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Repeat color="action" />
          <Typography variant="h5" fontWeight={700}>
            {t("recurringOrders.title")}
          </Typography>
        </Stack>

        <ToggleButtonGroup
          size="small"
          exclusive
          value={statusFilter}
          onChange={(_e, val) => {
            if (val !== null) {
              setStatusFilter(val)
              setPaginationModel((m) => ({ ...m, page: 0 }))
            }
          }}
        >
          <ToggleButton value="all">{t("recurringOrders.filter.all")}</ToggleButton>
          <ToggleButton value="active">{t("recurringOrders.filter.active")}</ToggleButton>
          <ToggleButton value="stopped">{t("recurringOrders.filter.stopped")}</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {/* DataGrid */}
      <Paper {...uiSurface("UI-DLV2-141")} variant="outlined" sx={{ flex: 1, borderRadius: 2 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={isLoading}
          rowCount={rowCount}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          pageSizeOptions={[10, 25, 50]}
          disableRowSelectionOnClick
          onRowClick={(params) => setSelectedId(params.row.id)}
          sx={{
            border: 0,
            "& .MuiDataGrid-row": { cursor: "pointer" },
          }}
          localeText={{
            noRowsLabel: t("recurringOrders.noRecurringOrders"),
          }}
        />
      </Paper>

      {/* Detail Drawer */}
      {selectedId !== null && (
        <RecurringOrderDetailDrawer
          recurringOrderId={selectedId}
          open={selectedId !== null}
          onClose={() => setSelectedId(null)}
          onSnackbar={showSnackbar}
        />
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}
